import { createSelector } from 'reselect'
import { List } from 'immutable'
import { safeToJS, getIn, getInOr } from 'utils/immutable'
import { identity, isEmpty } from 'ramda'
import { getMinorVersion, handlePreVersusBeta } from 'utils/utils'
import { createToJsSelector } from './utils'

const stateSelector = (state) => state.get('application')

export const getPageMeta = createSelector(
  stateSelector,
  (applicationState) => ({
    header: applicationState.get('header'),
    headerHover: applicationState.get('headerHover'),
    subHeader: applicationState.get('subHeader'),
    subHeaderOnTop: applicationState.get('subHeaderOnTop'),
    back: {
      text: applicationState.get('backText'),
      action: applicationState.get('backAction'),
    },
    notifications: applicationState.get('notifications'),
    appVersion: applicationState.get('appVersion'),
    errorMessages: applicationState.get('errorMessages'),
    errorCode: applicationState.get('errorCode'),
    showModal: applicationState.get('showModal'),
    overrideDefaultPageMeta: applicationState.get('overrideDefaultPageMeta'),
    selectedAppSection: applicationState.get('selectedAppSection'),
    hasSelectedAppSection: applicationState.get('hasSelectedAppSection'),
  })
)

export const getApplication = createSelector(
  stateSelector,
  (applicationState) => ({
    appVersion: applicationState.get('appVersion'),
    windows: applicationState.get('windows')
      ? applicationState.get('windows').toJS()
      : null,
    modals: applicationState.get('modals'),
  })
)

const getAsyncSet = (state) => state.getIn(['application', 'asyncSet'])
const getErrorDictionary = (state) =>
  safeToJS(state.getIn(['application', 'errorDictionary']))

export const makeLoadingSelector = ({ asyncKeys }) =>
  createSelector(getAsyncSet, (queue) =>
    asyncKeys.some((key) => queue.has(key))
  )

export const getIsLoading = createSelector(
  getAsyncSet,
  (_, { asyncKeys }) => asyncKeys,
  (asyncSet, asyncKeys = []) => asyncKeys.some((key) => asyncSet.has(key))
)

export const getAsyncInfo = createSelector(
  getAsyncSet,
  getErrorDictionary,
  (waitingSignals, errors) => ({ waitingSignals, errors })
)

export const makeGetIsWaiting = () =>
  createSelector((state, { signals }) => {
    const asyncSet = state.getIn(['application', 'asyncSet'])
    return signals.some((signal) => asyncSet.has(signal))
  }, identity)

const uiVersionSelector = getIn(['application', 'appVersion'])
export const getUIVersionSelector = createSelector(uiVersionSelector, identity)

export const getMinorUIVersion = createSelector(
  getUIVersionSelector,
  (version) => {
    try {
      const minor = getMinorVersion(version)
      return minor
    } catch (e) {
      console.warn(e)
      return 0
    }
  }
)

const getApplicationState = (state) => state.getIn(['application'])

export const getSelectedAppSection = createSelector(
  getApplicationState,
  (applicationState) => ({
    selectedAppSection: applicationState.get('selectedAppSection'),
  })
)

export const getHasSelectedAppSection = createSelector(
  getApplicationState,
  (applicationState) => applicationState.get('hasSelectedAppSection')
)

export const getNotificationPanelState = createSelector(
  getApplicationState,
  (applicationState) => applicationState.get('notificationPanelState')
)

const notificationsSelector = getInOr(List(), ['application', 'notifications'])
export const getHasPersistentNotifictions = createSelector(
  notificationsSelector,
  (notifications) => {
    if (isEmpty(notifications)) return false
    const persistentNotifications = notifications.filter((notification) =>
      notification.get('persistent')
    )
    return !persistentNotifications.isEmpty()
  }
)

export const getPreMinorUIVersion = createSelector(
  getUIVersionSelector,
  (version) => {
    try {
      const preMinor = handlePreVersusBeta(version)
      return preMinor
    } catch (e) {
      console.warn(e)
      return 0
    }
  }
)

export const getAllNotifications = createToJsSelector(
  createSelector(notificationsSelector, (notifications) => notifications)
)

// export const getNotificationByIdIfExists = createToJsSelector
